
import { Options, Vue } from "vue-class-component";
import { mapActions, mapGetters } from "vuex";
import moment, { MomentInput } from "moment";
import { computed, ref } from "vue";
import AlertFeedbackSelector from "./AlertFeedbackSelector.vue";
import {
  EquipmentMlStatus,
  FeedbackEnum,
} from "../../types/equipment-ml-status";
import LinksCard from "../equipment-details/LinksCard.vue";
import { Equipment } from "../../types/equipment";
import { UserRole } from "../../types/user-info";
import { isUserUnknown } from "../../helpers/user-role-helper";
import RealAlertPopin from "./RealAlertPopin.vue";
import { downloadFile } from "../../helpers/file-upload-helper";
import NormalBehaviorPopin from "./NormalBehaviorPopin.vue";
import { alertFilterOptions } from "../../helpers/equipment-alerting-helper";
import {
  isSuccess,
  getMessageByStatusCode,
  treatErrors,
} from "../../helpers/http-status-helper";
import EquipmentApi from "@/api/equipment";
import { formatDateWithoutHours } from "../../helpers/equipment-status-helpers";
import { debounce } from "lodash";

@Options({
  computed: {
    ...mapGetters([
      "getEquipmentMlStatuses",
      "getTotalAlertCount",
      "getEquipmentList",
      "getEquipmentDetails",
      "getUserRole",
      "getSasToken",
      "getSelectedSite",
      "getSelectedCountryBloc",
    ]),
  },
  methods: {
    ...mapActions([
      "equipmentAlertingPageLoaded",
      "updateEquipmentMlStatus",
      "updateEquipmentDetails",
      "equipmentDetailsReset",
      "displayRealAlertPopin",
      "claimSasToken",
      "displayNormalBehaviorPopin",
      "displayHttpErrorDialog",
    ]),
    formatDate(date: MomentInput) {
      return moment(date).format("YYYY/MM/DD - HH:mm");
    },
  },
  components: {
    AlertFeedbackSelector,
    LinksCard,
    RealAlertPopin,
    NormalBehaviorPopin,
  },
  data() {
    return {
      selectedAlertFilter: undefined,
      alertFilterOptions,
      equipmentMlStatusesClone: [],
      showExportOptions: false,
      columns: [
        {
          name: "unread",
          align: "left",
          field: "unread",
        },
        {
          name: "date",
          label: "Date - Time (1st drift) ",
          align: "left",
          field: "date",
          sortable: true,
        },
        {
          name: "equipment",
          align: "left",
          label: "Equipment",
          field: "equipmentName",
        },
        {
          name: "type",
          align: "left",
          label: "Alert classification",
          field: "feedback",
        },
        {
          name: "downloadPlot",
          field: "plotPath",
        },
        {
          name: "expand",
          align: "left",
          label: "",
        },
      ],
      equipmentTotalAlertCount: 0,
      loading: false,
      pagination: {
        page: 1,
        sortBy: "date",
        descending: true,
        rowsPerPage: 10,
        rowsNumber: 0,
      },
    };
  },
  name: "AlertsCard",
})
export default class AlertsCard extends Vue {
  equipmentAlertingPageLoaded!: ({
    skip,
    limit,
    filter,
    filterByAlert,
  }: any) => Promise<void>;
  getUserRole!: UserRole;
  showExportOptions: boolean;
  getEquipmentMlStatuses!: EquipmentMlStatus[];
  getTotalAlertCount: number;
  equipmentMlStatusesClone!: EquipmentMlStatus[];
  equipmentTotalAlertCount: number;
  getEquipmentList!: Equipment[];
  getSelectedSite!: string;
  getSelectedCountryBloc!: string;
  updateEquipmentDetails!: (equipment: Equipment | undefined) => void;
  equipmentDetailsReset!: () => void;
  getEquipmentDetails!: Equipment;
  updateEquipmentMlStatus!: ({
    equipmentId,
    sensorStatusId,
    unread,
    feedback,
  }: any) => Promise<boolean>;
  filter = "";
  selectedAlertFilter!: FeedbackEnum | undefined;
  displayRealAlertPopin!: (displayRealAlertPopin: boolean) => void;
  displayNormalBehaviorPopin!: (displayNormalBehaviorPopin: boolean) => void;
  displayHttpErrorDialog!: ({
    displayHttpErrorDialog,
    httpErrorMessage,
  }: {
    displayHttpErrorDialog: boolean;
    httpErrorMessage: string;
  }) => void;

  claimSasToken!: () => Promise<void>;
  getSasToken!: string;
  pagination: any;
  loading: boolean;
  private debouncedLoad!: () => void;

  rowClass(unread: boolean): string {
    if (unread) return "text-bold";
    else return "";
  }
  async updateStatus(
    equipmentId: number,
    equipmentMlStatusId: number,
    unread: boolean,
    feedback: FeedbackEnum
  ): Promise<void> {
    this.updateEquipmentMlStatus({
      equipmentId,
      equipmentMlStatusId,
      unread,
      feedback,
    });
  }

  async mounted(): Promise<void> {
    this.debouncedLoad = debounce(() => {
      this.pagination.page = 1;
      this.onRequest({ pagination: this.pagination });
    }, 500);
    this.$watch(
      "filter",
      () => {
        this.pagination.page = 1;
        this.debouncedLoad();
      },
      { immediate: false }
    );
    this.$watch(
      "selectedAlertFilter",
      () => {
        this.pagination.page = 1;
        this.debouncedLoad();
      },
      { immediate: false }
    );
    this.$q.loading.show();
    this.onEquipmentNameClick(this.getEquipmentMlStatuses[0]);
    await this.onRequest({
      pagination: this.pagination,
      filter: undefined,
    });
    this.$q.loading.hide();
  }
  onKeywordInput() {
    this.pagination.page = 1;
    this.debouncedLoad();
  }

  onKeywordClear() {
    this.filter = "";
    if (this.debouncedLoad && (this.debouncedLoad as any).cancel) {
      (this.debouncedLoad as any).cancel();
    }
    this.pagination.page = 1;
    this.onRequest({ pagination: this.pagination });
  }

  async onRequest(props) {
    let { page, rowsPerPage, rowsNumber, sortBy, descending } =
      props.pagination;

    this.loading = true;
    const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
    const limit = this.pagination.rowsPerPage;
    const payload: {
      skip: number;
      limit: number;
      filter?: string;
      filterByAlert?: string;
    } = { skip, limit };
    if (this.filter && this.filter.trim()) {
      payload.filter = this.filter.trim();
    }
    if (this.selectedAlertFilter && this.selectedAlertFilter.trim()) {
      payload.filterByAlert = this.selectedAlertFilter.trim();
    }
    await this.equipmentAlertingPageLoaded(payload);
    // await this.equipmentAlertingPageLoaded({
    //   skip: (props.pagination.page - 1) * props.pagination.rowsPerPage,
    //   limit: props.pagination.rowsPerPage,
    // });
    this.equipmentMlStatusesClone = this.getEquipmentMlStatuses;
    this.pagination.rowsNumber = this.getTotalAlertCount;

    this.pagination.page = page;
    this.pagination.rowsPerPage = rowsPerPage;
    this.pagination.sortBy = sortBy;
    this.pagination.descending = descending;

    this.loading = false;
  }

  onSearchIconClick() {
    if (this.debouncedLoad && (this.debouncedLoad as any).cancel) {
      (this.debouncedLoad as any).cancel();
    }
    this.pagination.page = 1;
    this.onRequest({ pagination: this.pagination });
  }

  get currentSelectedEquipment(): Equipment | undefined {
    return this.getEquipmentDetails;
  }

  onEquipmentNameClick(equipmentMLStatus: EquipmentMlStatus): void {
    if (equipmentMLStatus) {
      this.updateEquipmentDetails(equipmentMLStatus.equipment);
    }
  }

  unmounted(): void {
    this.equipmentDetailsReset();
  }

  isUserUnknownOrGuestOrOffshore(): boolean {
    return isUserUnknown(this.getUserRole);
  }

  private piVisionLink(sensorTag: string): void {
    const locationMap: Record<string, string> = {
      angola: "https://pi-vision-ao.ep.corp.local/PIVision/#",
      argentina: "https://pi-vision-ar.ep.corp.local/PIVision/#",
      congo: "https://pi-vision-cg.ep.corp.local/PIVision/#",
      denmark: "https://pi-vision-dk.ep.corp.local/PIVision/#",
      nigeria: "https://pi-vision-los-ng.ep.corp.local/PIVision/#",
      nederland: "https://pi-vision-nl.ep.corp.local/PIVision/#",
      usa: "https://pivision-p-pta.main.glb.corp.local/PIVision/#",
      hq: "https://pi-vision-hq.ep.corp.local/PIVision/#",
      default:
        " https://pivision-p-pld.main.glb.corp.local/PIVision/#/Displays/",
    };

    const prefixMap: Record<string, string> = {
      angola: "LAD-AO",
      argentina: "BUE-AR",
      congo: "PNR-CG",
      denmark: "EBJ-DK",
      nigeria: "LOS-NG",
      nederland: "DHG-NL",
      usa: "PTA",
      hq: "HQ",
      default: "",
    };

    const parts = this.$route.path.split("/");
    const rawCountry = decodeURIComponent(parts[1] || "").trim();
    const countryKey = rawCountry.includes("-")
      ? rawCountry.split("-")[0].toLowerCase()
      : rawCountry.toLowerCase();

    const baseUrl = locationMap[countryKey] || locationMap.default;
    const prefix = prefixMap[countryKey] || prefixMap.default;
    if (!prefix) {
      console.log("baseurl", baseUrl);
      window.open(baseUrl, "_blank");
      return;
    }
    const trimmedTag = (sensorTag || "").trim();
    if (!trimmedTag) {
      console.log("baseurl", baseUrl);

      window.open(baseUrl, "_blank");
      return;
    }
    const daSegment = countryKey === "usa" ? "" : "-DA";
    const prefixString = `\\\\PI${daSegment}-${prefix}\\`;
    const encodedPrefix = encodeURIComponent(prefixString);
    const encodedTag = encodeURIComponent(trimmedTag);
    const fullUrl = `${baseUrl}/Displays/adhoc?dataitems=${encodedPrefix}${encodedTag}`;
    console.log("opening PI Vision URL:", fullUrl);
    window.open(fullUrl, "_blank");
  }

  async downloadPlot(plotPath: string | undefined): Promise<void> {
    this.showExportOptions = false;
    if (plotPath) {
      this.$q.loading.show();
      await this.claimSasToken();
      const token = this.getSasToken;
      if (token) {
        await downloadFile(token, "anomaly-detection-plots", plotPath);
      }
      this.$q.loading.hide();
    }
  }
  async onExportData(format: "excel" | "json"): Promise<void> {
    (this.showExportOptions = false), this.$q.loading.show();
    try {
      const result = await EquipmentApi.exportEquipmentAlertData(
        this.getSelectedCountryBloc,
        this.getSelectedSite
      );
      // if (isSuccess(result.status)) {
      //   const mimeType = format === 'json' ? 'application/json': 'text/csv;charset=utf-8';
      //   const extension = format === 'json' ? 'json' : 'csv';

      //   const blob = new Blob([result.data], {type: mimeType});
      //   const url = window.URL.createObjectURL(blob);
      //   const link = document.createElement("a");
      //   link.href = url;

      //   link.setAttribute(
      //     "download",
      //     `OneCBM-Alert-Report-${this.getSelectedCountryBloc}-${
      //       this.getSelectedSite
      //     }-${formatDateWithoutHours(new Date())}.csv`
      //   );
      //   document.body.appendChild(link);
      //   link.click();
      // }
      if (isSuccess(result.status)) {
        const filename = `OneCBM-Alert-Report-${this.getSelectedCountryBloc}-${
          this.getSelectedSite
        }-${formatDateWithoutHours(new Date())}`;

        if (format === "excel") {
          // Save CSV directly
          const blob = new Blob([result.data], {
            type: "text/csv;charset=utf-8;",
          });
          this.downloadBlob(blob, `${filename}.csv`);
        } else if (format === "json") {
          // Convert CSV to JSON and save
          const csvText = await this.blobToText(result.data);
          const json = this.csvToJson(csvText);
          const jsonBlob = new Blob([JSON.stringify(json, null, 2)], {
            type: "application/json",
          });
          this.downloadBlob(jsonBlob, `${filename}.json`);
        }
      }
    } catch (err: any) {
      this.displayHttpErrorDialog({
        displayHttpErrorDialog: true,
        httpErrorMessage:
          "Export alert list : " + getMessageByStatusCode(treatErrors(err)),
      });
    }
    this.$q.loading.hide();
  }

  blobToText(blobData: any): Promise<string> {
    return new Response(blobData).text();
  }

  csvToJson(csvText: string): any[] {
    const lines = csvText.trim().split("\n");
    const headers = lines[0].split(",");
    return lines.slice(1).map((line) => {
      const values = line.split(",");
      const entry: Record<string, string> = {};
      headers.forEach((header, i) => {
        entry[header.trim()] = values[i]?.trim();
      });
      return entry;
    });
  }

  downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  set filterByAlert(feedback: FeedbackEnum | undefined) {
    this.equipmentMlStatusesClone = this.getEquipmentMlStatuses;
    if (feedback === undefined) {
      this.equipmentMlStatusesClone = this.equipmentMlStatusesClone.filter(
        (equipmentMLStatus: EquipmentMlStatus) =>
          equipmentMLStatus.feedback !== FeedbackEnum.NORMAL_BEHAVIOR &&
          equipmentMLStatus.feedback !== FeedbackEnum.SPURIOUS &&
          equipmentMLStatus.feedback !== FeedbackEnum.REAL
      );
    } else if (feedback !== null) {
      this.equipmentMlStatusesClone = this.equipmentMlStatusesClone.filter(
        (equipmentMLStatus: EquipmentMlStatus) =>
          equipmentMLStatus.feedback === feedback
      );
    }

    this.selectedAlertFilter =
      feedback === undefined ? FeedbackEnum.Empty_Unknown : feedback;
  }

  get filterByAlert(): FeedbackEnum | undefined {
    return this.selectedAlertFilter;
  }
}
